'use client';

import { useQueryState, useQueryStates } from 'nuqs';
import type * as nuqs from 'nuqs';
import { std } from '@funkey/shared/utils';
import { useCallback, useMemo, useRef, type FC, type PropsWithChildren } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useI18n } from '~/features/i18n';
import { contextFactory } from '~/utils/react';
import { LocalJSONStorage } from '~/utils/js';
import type { ActivitySearchPage } from '../server';
import { ActivitySearchModeBridge } from '../ActivitySearchModeBridge';
import { ActivityAiAutoPrompt } from '../ActivityAiAutoPrompt';
import {
    ACTIVITY_FILTER_PAGINATION_QUERY_PARAMS,
    ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS,
    Activity_Search_QueryParams,
} from '../shared';
import { NEAR_POINT_CLEARED, PrefilledNearPointProvider } from './nearPoint';

export const MAX_PARTICIPANTS = 10_000;

export const CACHED_PARTICIPANTS_FILTER = new LocalJSONStorage(
    'FILTER_PARTICIPANTS',
    20,
    'localStorage',
);

type CachedRegionCitySearch = {
    address: string;
    lat: number;
    lng: number;
    radiusKm: number;
} | null;

export const CACHED_REGION_CITY_SEARCH = new LocalJSONStorage<CachedRegionCitySearch>(
    'FILTER_REGION_CITY_SEARCH',
    null,
    'localStorage',
);

const NUQS_OPTIONS = ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS;

/**
 * The one event that says a visitor changed a filter.
 *
 * Filter use could only be read from the URL before this, which the experiment
 * cannot work with: the participant filter now defaults to 20 without writing
 * it to the URL, so a default and a choice look the same there, and a URL never
 * says in which order the filters were reached.
 *
 * Fired from the context every filter mutation goes through, so it cannot fire
 * on mount or on a URL parse — only a setter call reaches here. It ships to
 * both groups: a baseline read one way for the variant and another for the
 * control is not a baseline.
 */
/** Filters whose value is text the visitor typed or a place they are standing:
 * free-text search, the address behind the location filter and its coordinates.
 * What is interesting for the experiment is that the filter was used, not what
 * was in it, so the value is replaced by a marker rather than shipped. */
const UNTRACKED_FILTER_VALUES = new Set(['query', 'fq', 'near', 'nearLabel', 'nearLat', 'nearLng']);

function redactFilterValue(filter: string, value: unknown): unknown {
    if (!UNTRACKED_FILTER_VALUES.has(filter)) return value;
    return value === null ? null : '<set>';
}

/**
 * What "clear all" writes over these filters instead of `null`.
 *
 * nuqs answers a `null` with the parser's default, and a region or a city page
 * sets its own defaults — so nulling the region handed that same region straight
 * back and the page looked unclearable. An empty array beats an array default and
 * reads as "nothing selected" everywhere there is no page default to beat.
 */
const CLEARED_FILTER_VALUES = {
    regions: [],
    categories: [],
    subCategories: [],
    location: [],
    languages: [],
    locationType: [],
    season: [],
    cleanliness: [],
    teamwork: [],
    action: [],
    competition: [],
    learning: [],
} satisfies Partial<Activity_Search_QueryParams>;

function usePlpFilterChangeTracking() {
    const posthog = usePostHog();
    // Nth change this visitor made, so a funnel can ask what they touched first.
    const sequence = useRef(0);

    return useCallback(
        (
            patch: Record<string, unknown>,
            previous: Record<string, unknown>,
            source: 'bar' | 'sidebar' | 'drawer' | 'pill' | 'clear_all',
        ) => {
            for (const [filter, value] of Object.entries(patch)) {
                const next = value ?? null;
                const before = previous[filter] ?? null;
                // `clear()` writes null over every filter key, including the ones
                // that were already null. Counting those as changes would inflate
                // the baseline with events nobody caused.
                if (JSON.stringify(next) === JSON.stringify(before)) continue;

                sequence.current += 1;
                posthog.capture('plp.filter.change', {
                    filter,
                    value: redactFilterValue(filter, next),
                    previous_value: redactFilterValue(filter, before),
                    sequence: sequence.current,
                    source,
                });
            }
        },
        [posthog],
    );
}

namespace useActivitySearchCtx {
    export type Ctx = ReturnType<typeof useActivitySearchCtx>;

    export type AllFiltersState = ReturnType<Ctx['useFilters']>;
    export type FilterValues = AllFiltersState['value'];
}

const ActivitySearchProviderInner: FC<
    PropsWithChildren<{ hasPrefilledNearPoint: boolean }>
> = props => (
    <PrefilledNearPointProvider value={props.hasPrefilledNearPoint}>
        <ActivitySearchModeBridge />
        <ActivityAiAutoPrompt />
        {props.children}
    </PrefilledNearPointProvider>
);

const [useActivitySearchCtx, ActivitySearchCtxProvider] = contextFactory(
    (
        props: ActivitySearchPage.EntryPointConfigProps & {
            hiddenFilters?: Array<'budget' | 'region'>;
            posthogEventCtx?: {
                'plp.view'?: {
                    /** Category ID if on a category specific page
                     *
                     * @TODO: Check if this is still used in posthog. If so: replace this annoyhing prop passing with posthog event groups
                     * */
                    category_id?: number;
                };
            };
        },
    ) => {
        const i18n = useI18n();

        const filters = useMemo(
            () =>
                Activity_Search_QueryParams._createParsers({
                    ...props.queryParamConfig?.defaultValues,
                    countryCode: props.queryParamConfig?.defaultValues?.countryCode || i18n.country,
                }),
            [i18n.country, props.queryParamConfig?.defaultValues],
        );

        const [page, setPage] = useQueryState(
            'page' satisfies keyof typeof ACTIVITY_FILTER_PAGINATION_QUERY_PARAMS,
            ACTIVITY_FILTER_PAGINATION_QUERY_PARAMS.page.withOptions(NUQS_OPTIONS),
        );

        type UseFiltersReturn<T extends nuqs.UseQueryStatesKeysMap<any>> = {
            value: nuqs.Values<T>;
            /** Partially update the filters */
            update: nuqs.SetValues<T>;
            /** Clear all filters to their default value */
            clear: () => ReturnType<nuqs.SetValues<T>>;

            getFilter<N extends Extract<keyof nuqs.Values<T>, string>>(
                name: N,
            ): {
                name: N;
                value: nuqs.Values<T>[N];
                setValue: React.Dispatch<React.SetStateAction<nuqs.Values<T>[N]>>;
            };
        };

        const trackFilterChange = usePlpFilterChangeTracking();

        const hasPrefilledNearPoint =
            props.queryParamConfig?.defaultValues?.nearLat != null &&
            props.queryParamConfig?.defaultValues?.nearLng != null;

        /**
         * What "clear all" writes over the filters.
         *
         * Null is not enough on a page that carries its own defaults: nuqs drops the
         * query param and hands the parser's default straight back, so a region page's
         * region and a city page's near point survived the clear. An empty array beats
         * an array default, and the near point needs the same flag the pill's own
         * remove sets.
         */
        return useMemo(() => {
            function useFilters(): UseFiltersReturn<typeof filters>;
            function useFilters<T extends keyof typeof filters>(
                ...names: T[]
            ): UseFiltersReturn<Pick<typeof filters, T>>;
            function useFilters<T extends keyof typeof filters>(
                ...names: T[]
            ): UseFiltersReturn<Pick<typeof filters, T>> {
                // Filter keys never contain a comma, so the joined key changes exactly when `names` does.
                const namesKey = names.join(',');
                const [value, update] = useQueryStates(
                    useMemo(() => {
                        if (names.length === 0) return filters;
                        return std.Object.pick(filters, names);
                    }, [namesKey]),
                    NUQS_OPTIONS,
                );

                return useMemo(() => ({
                    value,
                    update: (...args) => {
                        setPage(0);
                        const patch = typeof args[0] === 'function' ? args[0](value) : args[0];
                        if (patch) trackFilterChange(patch, value, 'bar');
                        return update(...args);
                    },
                    clear: () => {
                        setPage(0);
                        const patch = {
                            ...std.Object.fromEntries(
                                std.Object.keys(value).map(key => [key, null]),
                            ),
                            ...CLEARED_FILTER_VALUES,
                            ...NEAR_POINT_CLEARED({ hasPrefilledNearPoint }),
                        };
                        trackFilterChange(patch, value, 'clear_all');
                        return update(patch);
                    },

                    getFilter(name) {
                        return {
                            name,
                            value: value[name],
                            setValue(next: any) {
                                setPage(0);
                                const nextValue = typeof next === 'function' ? next(value[name]) : next;
                                trackFilterChange({ [name]: nextValue }, value, 'bar');
                                return update({ [name]: nextValue } as any);
                            },
                        } as any;
                    },
                } as const), [value, trackFilterChange]); // prettier-ignore
            }

            return {
                customization: props.customization,
                filters,
                hiddenFilters: props.hiddenFilters || [],
                hasPrefilledNearPoint,
                posthogEventCtx: props.posthogEventCtx ?? {},

                /** The index of the page */
                page: {
                    value: page,
                    setValue: setPage,
                },
                useFilters,
                useFilter<T extends keyof typeof filters>(name: T) {
                    type P = (typeof filters)[T];

                    type R = nuqs.UseQueryStateReturn<
                        nuqs.inferParserType<P>,
                        P extends { defaultValue: infer D } ? D : undefined
                    >;

                    const [value, setValue] = useQueryState(
                        name as any,
                        filters[name],
                    ) as unknown as R;

                    return useMemo<{
                        value: R[0]
                        setValue: R[1]
                    }>(() => ({
                        name,
                        value,
                        setValue: (...args) => {
                            setPage(0)
                            const next = typeof args[0] === 'function' ? (args[0] as any)(value) : args[0];
                            trackFilterChange({ [name]: next }, { [name]: value }, 'bar');
                            return setValue(...args);
                        },
                    } as const), [value, trackFilterChange]); // prettier-ignore
                },
            } as const;
        }, [
            page,
            filters,
            trackFilterChange,
            hasPrefilledNearPoint,
            props.hiddenFilters,
            props.customization,
            props.queryParamConfig?.defaultValues?.nearLat,
            props.queryParamConfig?.defaultValues?.nearLng,
        ]);
    },
    ActivitySearchProviderInner,
);

export { useActivitySearchCtx, ActivitySearchCtxProvider };
